import { ThemeToggle } from "./ThemeToggle";
import { Ribbon, type Segment, type Mark } from "./Ribbon";
import type { PublicMaintenance } from "../api";

const STEPS: { n: string; title: string; body: string; healed?: boolean }[] = [
  {
    n: "01",
    title: "Name the fields",
    body: "A page address and the columns you want. Claude reads the live page and writes the extractor — no selector hunting.",
  },
  {
    n: "02",
    title: "It collects",
    body: "Plain CSS and XPath on a fast, deterministic path. Pull JSON or CSV, take a webhook, see what changed between runs.",
  },
  {
    n: "03",
    title: "It repairs itself",
    body: "When the markup shifts, AI rebuilds the selectors off the hot path, proves the fix on live runs, then switches over.",
    healed: true,
  },
];

/** Public pricing (mirrors PLANS in @amendie/schemas — the admin-ui has no schemas dep, so it's shown
    here by hand; keep in sync). Enterprise is a "talk to us", not a self-serve card. */
const PRICING: { id: string; name: string; price: string; period: string; features: string[]; featured?: boolean }[] = [
  {
    id: "free",
    name: "Free",
    price: "€0",
    period: "forever",
    features: ["2 scrapers", "100 successful runs / mo", "Every 6 hours", "7-day history"],
  },
  {
    id: "pro",
    name: "Pro",
    price: "€29",
    period: "/ month",
    features: ["15 scrapers", "3,000 successful runs / mo", "Hourly", "30-day history", "Webhooks"],
    featured: true,
  },
  {
    id: "business",
    name: "Business",
    price: "€99",
    period: "/ month",
    features: ["60 scrapers", "15,000 successful runs / mo", "Every 15 min", "90-day history", "Priority healing"],
  },
];

const ok = (n: number): Mark[] => Array<Mark>(n).fill("ok");
const DAY = 86_400_000;
const MIN = 60_000;
/**
 * The demo timeline (timestamps drive only the hover tooltip; marks are evenly spaced). The story, on
 * business time: the scraper runs once a day and stays clean for weeks. Then a site change breaks it —
 * two quick failed retries ~10 min apart, not a full day lost to noticing — and Amendie repairs the
 * extractor within the hour. The daily schedule resumes clean. A fast break → retry → heal, never a
 * multi-day gap. The many marks read as "running for a while"; the timestamps keep the failure short.
 */
function demoSegments(): Segment[] {
  const now = Date.now();
  const failAt = now - 24 * DAY; // the break, a few weeks back — clean days on either side
  // Up to the break: 30 clean daily runs, then two failed retries 10 min apart (same day).
  const before = Array.from({ length: 30 }, (_, i) => new Date(failAt - (30 - i) * DAY).toISOString());
  const fails = [new Date(failAt).toISOString(), new Date(failAt + 10 * MIN).toISOString()];
  // The heal lands ~20 min after the break; the daily schedule resumes clean, one per day, to ~now.
  const healAt = failAt + 20 * MIN;
  const after = Array.from({ length: 24 }, (_, i) => new Date(healAt + i * DAY).toISOString());
  return [
    { runs: [...ok(30), "fail", "fail"], runAt: [...before, ...fails] },
    { join: "heal", runs: ok(24), runAt: after },
  ];
}
const DEMO: Segment[] = demoSegments();

/**
 * Product front door — customers only. Sign in with Google (-> /auth/google/login, the live OIDC
 * flow). Nothing about operators/admin here by design; the break-glass admin-key gate lives behind
 * a direct link (/operator) that this page never surfaces. Shown whenever no session resolves.
 */
/**
 * @param authed  a signed-in user is peeking at the landing → offer a way back into the dashboard
 *                instead of a sign-in.
 * @param onEnter go (back) to the dashboard.
 */
export function Landing({
  authed = false,
  onEnter,
  maintenance,
}: {
  authed?: boolean;
  onEnter?: () => void;
  /** Public maintenance flags — when a tier is on, show a top banner + a "request access" link. */
  maintenance?: PublicMaintenance;
} = {}) {
  // The "sign-ups paused" turn-away is its OWN screen now (SignupsPaused), routed in App before the
  // landing renders — so it isn't woven into the front door and can't be missed. The banner here is the
  // AMBIENT signal (a visitor who lands but hasn't tried to sign in yet) — non-blocking, dismissable by
  // scrolling past. Lockdown copy differs from a mere sign-up pause.
  const maint = maintenance && (maintenance.fullLockdown || maintenance.maintenanceMode);
  return (
    <>
      {/* Full-bleed maintenance bar — flush to the very top of the page, ABOVE the centered content
          column (which is max-width'd + padded), so it reads as a real top lišta, not an inset box. */}
      {maint && (
        <div
          role="status"
          style={{
            background: "var(--gold-dim, #4a3a12)",
            color: "var(--gold, #e6c876)",
            borderBottom: "1px solid var(--rule, #333)",
            padding: "10px 16px",
            textAlign: "center",
            fontSize: "var(--t-cap, 0.9rem)",
            display: "flex",
            gap: 12,
            justifyContent: "center",
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <span>
            {maintenance!.fullLockdown
              ? "Amendie is temporarily down for maintenance."
              : "We're not taking new sign-ups right now."}
          </span>
          {/* Light-on-dark: a ghost button's dark text is unreadable on the dark banner, so pin gold
              text + border (matches the banner copy) with a transparent fill. */}
          <a
            className="btn sm"
            href="/request-access"
            style={{
              background: "transparent",
              color: "var(--gold, #e6c876)",
              border: "1px solid var(--gold, #e6c876)",
            }}
          >
            Request access
          </a>
        </div>
      )}
      <div className="landing-page">
      <div className="landing-top">
        <ThemeToggle iconOnly />
        {authed ? (
          <button className="btn ghost" onClick={onEnter}>
            Go to dashboard &rarr;
          </button>
        ) : (
          <a className="btn ghost" href="/login">
            Sign in
          </a>
        )}
      </div>

      <section className="hero">
        <div className="brand big">Amendie</div>
        <h1>Scrapers that heal.</h1>
        <p className="lede">
          Point Amendie at a page, name the fields, get clean rows on a schedule. When a site changes
          and extraction breaks, it rebuilds the extractor itself, verifies the fix on live runs, and
          keeps your data flowing — no 3&nbsp;a.m. alert, no manual patching.
        </p>

        {/* The demo is what earns the click, so it sits above the CTA. Hover any mark for what happened. */}
        <div className="hero-demo">
          <Ribbon segments={DEMO} height={56} />
        </div>

        <div className="hero-cta">
          {authed ? (
            <button className="btn lg" onClick={onEnter}>
              Go to dashboard &rarr;
            </button>
          ) : (
            <a className="btn lg" href="/login">
              Get started &rarr;
            </a>
          )}
        </div>
      </section>

      <section className="steps">
        {STEPS.map((s) => (
          <div className={s.healed ? "step healed" : "step"} key={s.n}>
            <div className="n">{s.n}</div>
            <h3>{s.title}</h3>
            <p>{s.body}</p>
          </div>
        ))}
      </section>

      <section className="pricing">
        <h2>You only pay for runs that return data.</h2>
        <p className="muted">
          Failed and self-healed runs are free — you&rsquo;re never billed because a site changed. Start
          free, upgrade any time.
        </p>
        <div className="price-grid">
          {PRICING.map((p) => (
            <div className={p.featured ? "price-card featured" : "price-card"} key={p.id}>
              <div className="price-name">{p.name}</div>
              <div className="price-amount">
                {p.price} <span className="muted sm">{p.period}</span>
              </div>
              <ul className="price-feats">
                {p.features.map((f) => (
                  <li key={f}>{f}</li>
                ))}
              </ul>
              <a className={p.featured ? "btn" : "btn ghost"} href="/login">
                Start free
              </a>
            </div>
          ))}
        </div>
        <p className="muted sm pricing-foot">
          Need more scale, SSO, or self-hosting? <b>Enterprise</b> is a conversation — reach out.
        </p>
      </section>
      </div>
    </>
  );
}
